import type { ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import { HelpPopover } from "./HelpPopover";

// Slider (range) con badge de valor + labels opcionales de extremos. Unifica
// el patron range+badge que estaba copiado a mano ~5 veces en GlobalSettings
// y el <Slider> de Configuracion (que ya soportaba decimales/porcentaje).
//
// El valor se muestra UNA sola vez (el badge): el dueño pidio no duplicar el
// numero con un <input> aparte. La ayuda verbosa va detras de un "?" (prop
// `help`) en vez de un parrafo siempre visible.
export function SliderField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  minLabel,
  maxLabel,
  displayDecimals = 0,
  displayAsPercent = false,
  defaultValue,
  help,
  className,
}: {
  label: ReactNode;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  minLabel?: string;
  maxLabel?: string;
  displayDecimals?: number;
  displayAsPercent?: boolean;
  // Valor de referencia: el guardado al cargar la pagina. Cuando se pasa, se
  // muestra SIEMPRE en chico/tenue ("def 224") para que el usuario tenga la
  // referencia de como estaba; si el valor actual difiere, se vuelve un boton
  // para volver a el sin perderse.
  defaultValue?: number;
  help?: ReactNode;
  className?: string;
}) {
  // Formateo compartido para el valor actual y el de referencia, asi el "def"
  // respeta la misma unidad/decimales/porcentaje que el badge.
  const fmt = (v: number) =>
    displayAsPercent
      ? `${Math.round(v * 100)}%`
      : `${v.toFixed(displayDecimals)}${unit ? ` ${unit}` : ""}`;

  const display = fmt(value);

  // Epsilon relativo al step para que floats (ej step 0.05) no se marquen
  // "modificados" por ruido de coma flotante. Mitad de step = umbral seguro.
  const hasDefault = defaultValue !== undefined;
  const modificado =
    hasDefault && Math.abs(value - (defaultValue as number)) > step / 2;

  return (
    <div className={className ?? "mb-4 last:mb-0"}>
      <div className="mb-1 flex items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-1.5 font-medium">
          {label}
          {help && <HelpPopover>{help}</HelpPopover>}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {hasDefault &&
            (modificado ? (
              <button
                type="button"
                onClick={() => onChange(defaultValue as number)}
                title="Volver al valor guardado"
                className="flex items-center gap-1 rounded font-mono text-[10px] text-white/40 transition hover:text-white/80"
              >
                <RotateCcw className="h-3 w-3" />
                def {fmt(defaultValue as number)}
              </button>
            ) : (
              <span className="font-mono text-[10px] text-white/40">
                def {fmt(defaultValue as number)}
              </span>
            ))}
          <span className="rounded bg-white/10 px-2 py-0.5 font-mono text-xs">
            {display}
          </span>
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-[#a78bfa]"
      />
      {(minLabel || maxLabel) && (
        <div className="flex justify-between text-xs opacity-50">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
}
